import type { AnswerValue } from '@/lib/api/secure-links';
import { moduleNameKey } from '@/components/requirement-library/library-logic';

/**
 * Pure helpers of the client's secure-link pages (T-0191). The server
 * decides everything that counts (the name match, what is saved, what is
 * sent); these only shape the page and warn early.
 */

/**
 * The typed name against the person record, as the server compares them
 * (`namesMatch` in `@caliber-2/discovery-core`, Decision 54.38): case,
 * spacing, punctuation, accents and "&" versus "and" do not matter. Used to
 * enable the button; the server check is the one that counts.
 */
export function namesMatch(typed: string, fullName: string): boolean {
  const key = (v: string) =>
    moduleNameKey(v.normalize('NFKD').replace(/\p{M}+/gu, '')).replace(/ /g, '');
  const a = key(typed);
  return a.length > 0 && a === key(fullName);
}

/** An answer with nothing in it is no answer. */
export function isAnswered(value: AnswerValue | undefined): value is AnswerValue {
  if (!value) return false;
  if ('text' in value) return value.text.trim().length > 0;
  if ('options' in value) return value.options.length > 0;
  if ('option' in value) return value.option.length > 0;
  return true;
}

/** Only the answered values, as the draft and the submit take them. */
export function answeredValues(
  values: Record<string, AnswerValue>,
  questionIds: readonly string[],
): Record<string, AnswerValue> {
  const out: Record<string, AnswerValue> = {};
  for (const id of questionIds) {
    const v = values[id];
    if (isAnswered(v)) out[id] = v;
  }
  return out;
}

export interface StepComment {
  step: string;
  text: string;
}

/**
 * The client's per-step comments as the one comment the map routes store
 * (T-0182 hand-off): one "Step name: comment" line per step, in the order
 * the steps were first commented on.
 */
export function composeStepComments(
  comments: readonly StepComment[],
  stepName: (key: string) => string,
): string {
  const byStep = new Map<string, string[]>();
  for (const c of comments) {
    const text = c.text.replace(/\s+/g, ' ').trim();
    if (!text) continue;
    byStep.set(c.step, [...(byStep.get(c.step) ?? []), text]);
  }
  return [...byStep].map(([step, texts]) => `${stepName(step)}: ${texts.join(' ')}`).join('\n');
}

/** "Invoice to payment" reads "invoice to payment" mid-sentence; "AP intake" stays. */
export function midSentence(name: string): string {
  const first = name.split(/\s+/)[0] ?? '';
  if (first.length > 1 && first === first.toUpperCase()) return name;
  return name.charAt(0).toLowerCase() + name.slice(1);
}

/** "Dana Whitfield, Finance Director": the name and, when known, the title. */
export function nameAndTitle(fullName: string, title: string | null | undefined): string {
  return title ? `${fullName}, ${title}` : fullName;
}
